import { useState } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../lib/supabase';
import { useProfile } from './useProfile';
import type { StoredCV } from '../hooks/useCV';
import { useCV } from './useCV';
import { s3Service } from '../services/s3Service';

export const useFiles = () => {
	const { user } = useAuth();
	const { profile, updateProfile, updateCurrentCv } = useProfile(); // Ensure updateCurrentCv is imported
	const { cvs } = useCV();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const uploadFile = async (file: File, onUploadSuccess: () => void) => {
		setLoading(true);
		setError(null);
		try {
			if (!user) {
				throw new Error('Please login to continue');
			}

			// Get user token for S3 API calls
			const { data: { session } } = await supabase.auth.getSession();
			if (!session?.access_token) {
				throw new Error('No valid session found');
			}

			// Upload file to S3
			const uploadResult = await s3Service.uploadFile(file, session.access_token);

			// Store CV metadata in Supabase database with S3 key
			const { data: newCvData, error: cvError } = await supabase
				.from('cvs')
				.insert({
					user_id: user.id,
					filename: file.name,
					content: uploadResult.data.key, // Store S3 key instead of URL
					file_size: file.size,
					file_type: file.type,
				})
				.select()
				.single();

			if (cvError) throw cvError;

			// --- NEW LOGIC ---
			// Check if this is the user's first CV.
			// We can do this by checking if the profile's current_cv is not set,
			// or by counting the total CVs. Counting is more reliable.
			const { count, error: countError } = await supabase
				.from('cvs')
				.select('id', { count: 'exact', head: true })
				.eq('user_id', user.id);

			if (countError) {
				// Log the error but don't block the upload process
				console.error('Error counting user CVs:', countError.message);
			}

			// If this is the only CV the user has, set it as the current one.
			if (newCvData && count === 1) {
				await updateCurrentCv(newCvData.id);
			}
			// --- END NEW LOGIC ---

			onUploadSuccess(); // Callback to refetch CVs in the component
			return { data: uploadResult.data, error: null };
		} catch (err: any) {
			setError(err.message || 'Upload failed');
			return { data: null, error: err };
		} finally {
			setLoading(false);
		}
	};

	const deleteCV = async (cv: StoredCV, onDeleteSuccess: () => void) => {
		setLoading(true);
		setError(null);
		if (!user || !cv) {
			const msg = 'CV or user not found for deletion.';
			setError(msg);
			setLoading(false);
			return { error: new Error(msg) };
		}

		try {
			// Get user token for S3 API calls
			const { data: { session } } = await supabase.auth.getSession();
			if (!session?.access_token) {
				throw new Error('No valid session found');
			}

			// Check if content is an S3 key or old Supabase URL
			if (s3Service.isS3Key(cv.content)) {
				// Delete from S3
				try {
					await s3Service.deleteFile(cv.content, session.access_token);
				} catch (s3Error) {
					console.warn('S3 deletion error (continuing to delete from DB):', s3Error);
				}
			} else {
				// Handle old Supabase URLs (for migration compatibility)
				const pathSegment = cv.content.substring(
					cv.content.indexOf('/cv-match/') + '/cv-match/'.length,
				);
				const filePath = decodeURIComponent(pathSegment);

				const { error: deleteError } = await supabase.storage
					.from('cv-match')
					.remove([filePath]);

				if (deleteError) {
					console.warn('Storage deletion error (continuing to delete from DB):', deleteError);
				}
			}

			// 2. If deleted CV was the current one, update profile
			if (cv.id === profile?.current_cv) {
				await updateProfile({ current_cv: '' });
			}
			let idToUpdate: StoredCV[] | undefined;
			if(cvs?.length === 2) {
				idToUpdate = cvs?.filter((Scv) => Scv?.id !== cv?.id);
			}
			// 3. Delete from 'cvs' table
			const { error: deleteErrorTable } = await supabase
				.from('cvs')
				.delete()
				.eq('id', cv.id);

			if (deleteErrorTable) throw deleteErrorTable;
			if(cvs?.length === 2) {
				await updateProfile({ current_cv: idToUpdate?.[0]?.id ?? '' });
			}

			onDeleteSuccess(); // Callback to refetch CVs
			return { error: null };
		} catch (error: any) {
			console.error('Error deleting CV:', error);
			setError(error.message);
			return { error };
		} finally {
			setLoading(false);
		}
	};

	const downloadCV = async (cv: StoredCV) => {
		setLoading(true);
		setError(null);
		try {
			if (!user || !cv) {
				throw new Error('CV or user not found for download');
			}

			// Get user token for S3 API calls
			const { data: { session } } = await supabase.auth.getSession();
			if (!session?.access_token) {
				throw new Error('No valid session found');
			}

			// Check if content is an S3 key or old Supabase URL
			if (s3Service.isS3Key(cv.content)) {
				// Download from S3 using backend proxy
				await s3Service.downloadCVById(cv.id, session.access_token, cv.filename);
			} else {
				// Handle old Supabase URLs (for migration compatibility)
				// For old URLs, we can still use them directly
				window.open(cv.content, '_blank');
			}

			return { success: true, error: null };
		} catch (err: any) {
			setError(err.message || 'Download failed');
			return { success: false, error: err };
		} finally {
			setLoading(false);
		}
	};

	const getFileUrl = async (cv: StoredCV): Promise<string> => {
		try {
			if (!user || !cv) {
				throw new Error('CV or user not found');
			}

			// Get user token for S3 API calls
			const { data: { session } } = await supabase.auth.getSession();
			if (!session?.access_token) {
				throw new Error('No valid session found');
			}

			// Check if content is an S3 key or old Supabase URL
			if (s3Service.isS3Key(cv.content)) {
				// Get presigned URL from S3
				return await s3Service.getPresignedUrl(cv.content, session.access_token);
			} else {
				// Return old Supabase URL
				return cv.content;
			}
		} catch (err: any) {
			console.error('Error getting file URL:', err);
			throw err;
		}
	};

	return { uploadFile, deleteCV, downloadCV, getFileUrl, loading, error };
};